import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { pageMotion } from "../../motion/pageMotion";
import {
  Siren,
  Flame,
  Trees,
  CircleAlert,
  MapPin,
  CalendarClock,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import PageHeader from "../../page_header";
import RichTextView from "../../RichTextEditor/RichTextView";
import { guardianAlertService } from "../../../services/guardianAlertService";
import {
  classifyAlertType,
  normalizeTicketAction,
  pickAlertId,
  severityTone,
} from "../../../utils/alertResponseState";
import {
  PageShell,
  Intro,
  ResultCount,
  FilterChips,
  FilterChip,
  CardsGrid,
  AlertCard,
  SeverityRail,
  CardBody,
  CardHeader,
  TypeIconWrap,
  CardTitleBlock,
  CardTitle,
  CardSubtitle,
  MetaRow,
  Badge,
  NeutralBadge,
  CardText,
  CardMetaLine,
  CardActions,
  PrimaryButton,
  EmptyState,
  LoadingText,
} from "../styles";

const SEARCH_COLUMNS = [
  { value: "Código", label: "Código" },
  { value: "Tipo", label: "Tipo" },
  { value: "Área", label: "Área / Projeto" },
  { value: "Severidade", label: "Severidade" },
  { value: "Status", label: "Status" },
];

const FILTERS = [
  { id: "all", label: "Todos" },
  { id: "pending", label: "Aguardando ciência" },
  { id: "in_progress", label: "Em andamento" },
  { id: "done", label: "Respondidos" },
];

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function typeLabel(description) {
  const kind = classifyAlertType(description);
  if (kind === "incendio") return "Incêndio";
  if (kind === "desmatamento") return "Desmatamento";
  return description || "Outro";
}

function statusFromItem(item) {
  const action =
    item?.last_ticket?.action_display ||
    item?.last_ticket?.action ||
    "";
  const normalized = normalizeTicketAction(item?.last_ticket?.action);
  if (!normalized) return { bucket: "pending", label: "Aguardando ciência" };
  if (normalized === "respondido_processo_sucessao") {
    return { bucket: "done", label: action || "Respondido" };
  }
  if (
    normalized === "aguardando_resposta" ||
    normalized === "equipe_enviada" ||
    normalized === "resposta_rejeitado"
  ) {
    return {
      bucket: "in_progress",
      label:
        action ||
        (normalized === "equipe_enviada"
          ? "Equipe enviada"
          : normalized === "resposta_rejeitado"
            ? "Resposta rejeitada"
            : "Aguardando resposta"),
    };
  }
  return { bucket: "pending", label: action || "Aguardando ciência" };
}

function TypeIcon({ kind }) {
  if (kind === "incendio") return <Flame size={20} strokeWidth={2.2} />;
  if (kind === "desmatamento") return <Trees size={20} strokeWidth={2.2} />;
  return <CircleAlert size={20} strokeWidth={2.2} />;
}

/**
 * Home de PROP/GUARDIAN — lista de alertas com busca/filtros (padrão Projetos).
 */
export default function MyAlertsHome() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("Código");
  const [searchValue, setSearchValue] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await guardianAlertService.getAlertsByUser();
      setAlerts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Erro ao listar alertas do usuário:", err);
      setError("Não foi possível carregar seus alertas.");
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    return alerts.filter((item) => {
      const status = statusFromItem(item);
      if (filter !== "all" && status.bucket !== filter) return false;
      if (!term) return true;

      switch (selectedColumn) {
        case "Código":
          return String(item.cod_alerta || "")
            .toLowerCase()
            .includes(term);
        case "Tipo":
          return String(item.alert_type_description || "")
            .toLowerCase()
            .includes(term);
        case "Área":
          return (
            String(item.area_title || "")
              .toLowerCase()
              .includes(term) ||
            String(item.project_title || "")
              .toLowerCase()
              .includes(term)
          );
        case "Severidade":
          return String(item.severity_level_description || "")
            .toLowerCase()
            .includes(term);
        case "Status":
          return status.label.toLowerCase().includes(term);
        default:
          return true;
      }
    });
  }, [alerts, searchValue, selectedColumn, filter]);

  const openAlert = (item) => {
    const alertId = pickAlertId(item);
    if (!alertId) return;
    navigate(`/my-alerts/${alertId}`, {
      state: { alertPreview: item },
    });
  };

  return (
    <PageShell $wide $roomy>
      <PageHeader
        title="Meus alertas"
        actionLabel="Atualizar"
        onAction={load}
        actionDisabled={loading}
        search={{
          columns: SEARCH_COLUMNS,
          selectedColumn,
          onColumnChange: (e) => setSelectedColumn(e.target.value),
          searchValue,
          onSearchChange: (e) => setSearchValue(e.target.value),
          placeholder: "Pesquisar alertas…",
        }}
      />

      <Intro>
        Acompanhe e responda os alertas das suas áreas — ciência, equipe e
        providências no mesmo fluxo do aplicativo Guardian.
      </Intro>

      <FilterChips role="tablist" aria-label="Filtrar por status">
        {FILTERS.map((f) => (
          <FilterChip
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            $active={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </FilterChip>
        ))}
      </FilterChips>

      {!loading && !error ? (
        <ResultCount>
          {filtered.length === 0
            ? "Nenhum alerta neste filtro"
            : filtered.length === 1
              ? "1 alerta"
              : `${filtered.length} alertas`}
          {searchValue.trim() ? " · resultado da busca" : null}
        </ResultCount>
      ) : null}

      <AnimatePresence mode="wait">
      {loading ? (
        <motion.div
          key="alerts-loading"
          initial={pageMotion.initial}
          animate={pageMotion.animate}
          exit={pageMotion.exit}
          transition={pageMotion.transition}
        >
          <LoadingText>Carregando alertas…</LoadingText>
        </motion.div>
      ) : null}

      {error ? (
        <motion.div
          key="alerts-error"
          initial={pageMotion.initial}
          animate={pageMotion.animate}
          exit={pageMotion.exit}
          transition={pageMotion.transition}
          style={{ width: "100%" }}
        >
        <EmptyState>
          <Siren size={40} strokeWidth={1.75} />
          <strong>Não foi possível carregar</strong>
          <p>{error}</p>
          <PrimaryButton type="button" onClick={load}>
            <RefreshCw size={16} style={{ marginRight: 6, verticalAlign: -2 }} />
            Tentar novamente
          </PrimaryButton>
        </EmptyState>
        </motion.div>
      ) : null}

      {!loading && !error && filtered.length === 0 ? (
        <motion.div
          key={`empty-${filter}`}
          initial={pageMotion.initial}
          animate={pageMotion.animate}
          exit={pageMotion.exit}
          transition={pageMotion.transition}
          style={{ width: "100%" }}
        >
        <EmptyState>
          <Siren size={40} strokeWidth={1.75} />
          <strong>
            {alerts.length === 0
              ? "Nenhum alerta ativo"
              : "Nada encontrado"}
          </strong>
          <p>
            {alerts.length === 0
              ? "Quando houver ocorrências nas suas áreas, elas aparecerão aqui para você responder."
              : "Ajuste a busca ou o filtro de status para ver outros alertas."}
          </p>
        </EmptyState>
        </motion.div>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <motion.div
          key={filter}
          initial={pageMotion.initial}
          animate={pageMotion.animate}
          exit={pageMotion.exit}
          transition={pageMotion.transition}
          style={{ width: "100%" }}
        >
        <CardsGrid>
          {filtered.map((item) => {
            const alertId = pickAlertId(item);
            const typeDesc = item.alert_type_description || "";
            const severity = item.severity_level_description || "";
            const tone = severityTone(severity);
            const kind = classifyAlertType(typeDesc);
            const status = statusFromItem(item);
            const areaName =
              item.area_title ||
              item.area_name ||
              item.guardian_area?.name ||
              (typeof item.guardian_area === "object"
                ? item.guardian_area?.title
                : null);
            const projectName =
              item.project_title || item.guardian_area?.project?.title;
            const place =
              areaName || projectName
                ? [areaName, projectName].filter(Boolean).join(" · ")
                : "Área não informada";

            return (
              <AlertCard
                key={alertId || item.ticket_id || item.cod_alerta}
                role="button"
                tabIndex={0}
                onClick={() => openAlert(item)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openAlert(item);
                  }
                }}
              >
                <SeverityRail $tone={tone} aria-hidden />
                <CardBody>
                  <CardHeader>
                    <TypeIconWrap $tone={tone}>
                      <TypeIcon kind={kind} />
                    </TypeIconWrap>
                    <CardTitleBlock>
                      <CardTitle>
                        {item.cod_alerta
                          ? `Alerta ${item.cod_alerta}`
                          : typeLabel(typeDesc)}
                      </CardTitle>
                      <CardSubtitle>
                        <MapPin
                          size={13}
                          style={{
                            display: "inline",
                            verticalAlign: "-2px",
                            marginRight: 4,
                          }}
                        />
                        {place}
                      </CardSubtitle>
                    </CardTitleBlock>
                  </CardHeader>

                  <MetaRow>
                    <Badge $tone={tone}>{severity || "Severidade —"}</Badge>
                    <Badge $tone="low">{typeLabel(typeDesc)}</Badge>
                    <NeutralBadge $tone="low">{status.label}</NeutralBadge>
                  </MetaRow>

                  <CardText>
                    <RichTextView
                      value={item.observation}
                      fallback="Sem descrição adicional."
                    />
                  </CardText>

                  <CardMetaLine>
                    <CalendarClock
                      size={13}
                      style={{
                        display: "inline",
                        verticalAlign: "-2px",
                        marginRight: 4,
                      }}
                    />
                    {formatDate(item.alert_created_at)}
                  </CardMetaLine>
                </CardBody>

                <CardActions>
                  <PrimaryButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAlert(item);
                    }}
                  >
                    Ver ocorrência
                    <ChevronRight
                      size={16}
                      style={{ marginLeft: 4, verticalAlign: -3 }}
                    />
                  </PrimaryButton>
                </CardActions>
              </AlertCard>
            );
          })}
        </CardsGrid>
        </motion.div>
      ) : null}
      </AnimatePresence>
    </PageShell>
  );
}
